import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { barterApi, settingsApi } from '@/services/api'
import { useNavigate, Link } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import LocationSelect from '@/components/forms/LocationSelect'
import CategorySelect from '@/components/forms/CategorySelect'
import ImageUpload from '@/components/barters/ImageUpload'

export default function BarterCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    interested_in: '',
    type: 'goods',
    dollar_value: '',
    barter_coin_amount: '0',
    continent: '',
    country: '',
    state: '',
    city: '',
    visibility: 'active',
    categories: [] as string[],
  })
  const [barterAgreementAccepted, setBarterAgreementAccepted] = useState(false)
  const [escrowPolicyAccepted, setEscrowPolicyAccepted] = useState(false)
  const [error, setError] = useState('')
  const [createdBarterId, setCreatedBarterId] = useState<number | null>(null)
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [draftSaving, setDraftSaving] = useState(false)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load system settings for escrow percentage
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const escrowPercentage = parseFloat(settingsData?.data?.settings?.escrow_fee_percentage || '10') / 100

  // Load draft on mount
  const { data: draftData } = useQuery({
    queryKey: ['barter-draft'],
    queryFn: () => barterApi.getDraft(),
    staleTime: 0,
  })

  // Load images for the created barter
  const { data: imagesData } = useQuery({
    queryKey: ['barter-images', createdBarterId],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/barter-images?barter_id=${createdBarterId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      return response.json()
    },
    enabled: !!createdBarterId,
  })

  // Load draft into form when received
  useEffect(() => {
    if (draftData?.data?.draft && !draftLoaded) {
      const draft = draftData.data.draft
      setFormData({
        title: draft.title || '',
        description: draft.description || '',
        interested_in: draft.interested_in || '',
        type: draft.type || 'goods',
        dollar_value: draft.dollar_value?.toString() || '',
        barter_coin_amount: draft.barter_coin_amount?.toString() || '0',
        continent: draft.continent || '',
        country: draft.country || '',
        state: draft.state || '',
        city: draft.city || '',
        visibility: draft.visibility || 'active',
        categories: draft.categories || [],
      })
      setDraftLoaded(true)
    }
  }, [draftData, draftLoaded])

  // Auto-save draft (debounced)
  useEffect(() => {
    // Don't auto-save if barter was already created
    if (createdBarterId) return

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Only save if form has some content
    const hasContent = formData.title || formData.description || formData.dollar_value

    if (hasContent) {
      saveTimeoutRef.current = setTimeout(() => {
        setDraftSaving(true)
        barterApi.saveDraft(formData)
          .then(() => {
            setDraftSaving(false)
          })
          .catch((err) => {
            console.error('Failed to save draft:', err)
            setDraftSaving(false)
          })
      }, 3000) // Auto-save after 3 seconds of inactivity
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [formData, createdBarterId])

  const createMutation = useMutation({
    mutationFn: (data: any) => barterApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['barters'] })
      queryClient.invalidateQueries({ queryKey: ['barter-draft'] })
      const barterId = response.data?.barter?.id
      if (barterId) {
        setCreatedBarterId(barterId)
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create barter')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.categories.length === 0) {
      setError('Please select at least one category')
      return
    }

    const dollarValue = parseFloat(formData.dollar_value)
    if (dollarValue > 100000) {
      setError('Dollar value cannot exceed $100,000.00')
      return
    }

    if (!barterAgreementAccepted) {
      setError('You must accept the Barter Agreement to create a barter listing')
      return
    }

    if (!escrowPolicyAccepted) {
      setError('You must accept the Escrow Policy to create a barter listing')
      return
    }

    createMutation.mutate({
      ...formData,
      barter_agreement_accepted: barterAgreementAccepted,
      escrow_policy_accepted: escrowPolicyAccepted,
      barter_agreement_version: '1.0',
      escrow_policy_version: '1.0',
    })
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Create New Barter</h1>
        {draftSaving && (
          <span className="text-sm text-gray-500 italic">Saving draft...</span>
        )}
        {!draftSaving && draftLoaded && !createdBarterId && (
          <span className="text-sm text-green-600">✓ Draft auto-saved</span>
        )}
      </div>

      {draftLoaded && !createdBarterId && (
        <Alert
          type="info"
          message="Your previous draft has been restored. Continue editing below."
          onClose={() => setDraftLoaded(false)}
        />
      )}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {createdBarterId && (
        <Alert
          type="success"
          message="Barter created successfully! You can now add images below or skip to view your barter."
        />
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="space-y-6">
            <div>
              <label className="label">Title *</label>
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Professional Photography Services"
              />
            </div>

            <div>
              <label className="label">Description *</label>
              <textarea
                className="textarea"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Describe what you're offering in detail..."
              />
            </div>

            <div>
              <label className="label">Interested In</label>
              <textarea
                className="textarea"
                rows={3}
                value={formData.interested_in}
                onChange={(e) => setFormData({ ...formData, interested_in: e.target.value })}
                placeholder="What are you looking for in exchange? (e.g., web design, landscaping, furniture, etc.)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Help others know what you'd like to trade for
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Type *</label>
                <select
                  className="select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="goods">Goods</option>
                  <option value="services">Services</option>
                </select>
              </div>

              <div>
                <label className="label">Dollar Value *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100000"
                  className="input"
                  value={formData.dollar_value}
                  onChange={(e) => setFormData({ ...formData, dollar_value: e.target.value })}
                  required
                  placeholder="100.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const dollarValue = parseFloat(formData.dollar_value) || 0
                    if (dollarValue < 100) {
                      return '0% escrow required (0.00 coins). Maximum value: $100,000.00'
                    }
                    const escrowAmount = (dollarValue * escrowPercentage).toFixed(2)
                    const escrowPercent = (escrowPercentage * 100).toFixed(0)
                    return `${escrowPercent}% escrow required (${escrowAmount} coins). Maximum value: $100,000.00`
                  })()}
                </p>
              </div>
            </div>

            <div>
              <label className="label">Barter Coin Amount (Optional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="input"
                value={formData.barter_coin_amount}
                onChange={(e) => setFormData({ ...formData, barter_coin_amount: e.target.value })}
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">Additional coins to include with this barter</p>
            </div>

            <CategorySelect
              selected={formData.categories}
              onChange={(categories) => setFormData({ ...formData, categories })}
              required
            />

            <LocationSelect
              continent={formData.continent}
              country={formData.country}
              state={formData.state}
              city={formData.city}
              onContinentChange={(continent) =>
                setFormData({ ...formData, continent, country: '', state: '', city: '' })
              }
              onCountryChange={(country) =>
                setFormData({ ...formData, country, state: '', city: '' })
              }
              onStateChange={(state) =>
                setFormData({ ...formData, state, city: '' })
              }
              onCityChange={(city) => setFormData({ ...formData, city })}
            />

            <div>
              <label className="label">Visibility</label>
              <select
                className="select"
                value={formData.visibility}
                onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
              >
                <option value="active">Active (visible to others)</option>
                <option value="inactive">Inactive (hidden)</option>
              </select>
            </div>

            {/* Legal Agreements */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-gray-900">⚠️ Important Legal Agreements</h3>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-700 font-medium">
                  Before creating a barter listing, you must acknowledge and agree to the following:
                </p>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="barter-agreement"
                    checked={barterAgreementAccepted}
                    onChange={(e) => setBarterAgreementAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="barter-agreement" className="ml-2 text-sm text-gray-700">
                    I have read and agree to the{' '}
                    <Link
                      to="/legal/barter-agreement"
                      target="_blank"
                      className="text-primary-600 hover:text-primary-500 font-medium underline"
                    >
                      Barter Agreement
                    </Link>
                    . I understand that <strong>if I default on my obligations, my escrow will be forfeited and awarded to the other party as complete compensation</strong>, and I will not pursue further legal or social consequences.
                    <span className="text-red-600"> *</span>
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="escrow-policy"
                    checked={escrowPolicyAccepted}
                    onChange={(e) => setEscrowPolicyAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="escrow-policy" className="ml-2 text-sm text-gray-700">
                    I have read and agree to the{' '}
                    <Link
                      to="/legal/escrow-policy"
                      target="_blank"
                      className="text-primary-600 hover:text-primary-500 font-medium underline"
                    >
                      Escrow Policy
                    </Link>
                    . I understand that{' '}
                    {(() => {
                      const dollarValue = parseFloat(formData.dollar_value || '0')
                      if (dollarValue < 100) {
                        return <strong>no escrow is required for barters under $100.00</strong>
                      }
                      const escrowAmount = (dollarValue * escrowPercentage).toFixed(2)
                      const escrowPercent = (escrowPercentage * 100).toFixed(0)
                      return <><strong>{escrowPercent}% of my barter's dollar value ({escrowAmount} coins) will be held in escrow</strong>, and I acknowledge the fees, risks, and what can be lost if I default</>
                    })()}
                    .
                    <span className="text-red-600"> *</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className="mt-6 flex justify-end space-x-4">
          <Button type="button" variant="secondary" onClick={() => navigate('/dashboard/my-barters')}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={createMutation.isPending}
            disabled={createdBarterId !== null || !barterAgreementAccepted || !escrowPolicyAccepted}
          >
            Create Barter
          </Button>
        </div>
      </form>

      {/* Image Upload Section - Show after barter is created */}
      {createdBarterId && (
        <Card title="Barter Images" className="mt-6">
          <ImageUpload
            barterId={createdBarterId}
            images={imagesData?.data?.images || []}
            maxImages={25}
            canEdit={true}  // User owns the barter they just created
            onImagesChange={() => {
              // Refresh barter images after upload/delete
              queryClient.invalidateQueries({ queryKey: ['barter-images', createdBarterId] })
              queryClient.invalidateQueries({ queryKey: ['barters'] })
            }}
          />
          <div className="mt-6 flex justify-end space-x-4">
            <Button onClick={() => navigate('/dashboard/my-barters')}>
              Done - View My Barters
            </Button>
            <Button variant="primary" onClick={() => navigate(`/dashboard/barters/${createdBarterId}`)}>
              View Barter Details
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
